import { FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import TableRowName from '@/components/TableRowName/TableRowName';
import { PokerVariant } from '@/services/table/tableTypes';
import Modal from '@/components/Modal/Modal';
import { GameParams } from '@/services/table/tableState';
import PHNMTokenIcon from '@/components/PHNMTokenIcon';
import TableStakeInfo from '@/components/TableStakeInfo/TableStakeInfo';

const RoundInfoItem = ({
  features,
  variant,
  label,
  isLastItem,
  showHighLight,
}: {
  features: string[];
  variant: string;
  isFirstItem: boolean;
  isLastItem: boolean;
  showHighLight: boolean;
  label: string | ReactNode;
}) => {
  const hasFeatures = features && features.length > 0;

  return (
    <div className="flex w-full items-start gap-3">
      <div className="flex flex-col items-center">
        <PHNMTokenIcon className="my-1 h-4 w-4" />
        <div className={`h-[28px] w-[1px] ${!isLastItem ? 'bg-zinc-600' : ''}`} />
      </div>
      <div className="flex w-full items-start justify-between gap-2">
        <div className="flex flex-col">
          <span className={twMerge('text-sm font-bold text-white', !showHighLight && 'text-opacity-30')}>
            {variant}
          </span>
          <span className={twMerge('text-xs font-bold', hasFeatures ? 'text-ph-purple' : 'text-white text-opacity-30')}>
            {hasFeatures ? features.join(', ') : ' '}
          </span>
        </div>
        <span className="text-right text-sm font-bold text-white text-opacity-50">{label}</span>
      </div>
    </div>
  );
};

interface TableInfoModalProps {
  open: boolean;
  setClose: () => void;
  currentPlayers: number;
  maxPlayers: number;
  currentVariant?: PokerVariant;
  name: string;
  variants: PokerVariant[];
  gameParams: GameParams[];
}

const TableInfoModal: FC<TableInfoModalProps> = ({
  open,
  setClose,
  currentPlayers,
  maxPlayers,
  currentVariant,
  name,
  variants,
  gameParams,
}) => {
  return (
    <Modal
      open={open}
      setClose={setClose}
      className="lg:min-w-[500px]"
      titleComponent={<Modal.Title title="Table Info" onClose={setClose} />}
    >
      <div className="flex flex-col gap-5">
        <div className="w-full rounded-xl bg-phenom-border-glow p-[1px]">
          <div className="h-full w-full rounded-xl bg-ph-background-dark px-4 py-3">
            <TableRowName
              players={currentPlayers}
              maxPlayers={maxPlayers}
              variant={currentVariant?.name || ''}
              name={name}
            />
          </div>
        </div>
        {variants && (
          <div className="flex flex-col gap-0">
            {gameParams.map((gp, index) => {
              const variant = variants[gp.index];
              const features: string[] = [];
              if (gp.ante) features.push('Ante');
              if (gp.bettingCap) features.push('Capped');

              return (
                <RoundInfoItem
                  key={gp.index}
                  label={
                    <TableStakeInfo
                      fontSize="text-[12px]"
                      ante={gp.ante}
                      smallBet={gp.smallBet}
                      bigBet={gp.bigBet}
                      smallBlind={gp.smallBlind}
                      bigBlind={gp.bigBlind}
                    />
                  }
                  variant={variant.name}
                  features={features}
                  isFirstItem={index === 0}
                  isLastItem={index === gameParams.length - 1}
                  showHighLight={variant.id === currentVariant?.id}
                />
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default TableInfoModal;
